import React, { useState, useRef, useEffect } from "react";
import { apiFetch, API_BASE } from "./utils/api";
import "./MyTickets.css";

// Quill stores the description as raw HTML. Whitelist the tags/attributes it
// can actually produce before rendering, so nothing injected through the API
// can execute here.
const ALLOWED_TAGS = new Set([
    "P", "BR", "STRONG", "B", "EM", "I", "U", "S", "STRIKE",
    "OL", "UL", "LI", "SPAN", "DIV",
]);
const ALLOWED_ATTRS = new Set(["class", "style"]);
const DROP_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META"]);

const sanitizeHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");

    const walk = (node) => {
        Array.from(node.children).forEach((el) => {
            if (DROP_TAGS.has(el.tagName)) {
                el.remove();
                return;
            }
            if (!ALLOWED_TAGS.has(el.tagName)) {
                // Keep the text, drop the wrapper
                el.replaceWith(...el.childNodes);
                return;
            }
            Array.from(el.attributes).forEach((attr) => {
                if (!ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
                    el.removeAttribute(attr.name);
                }
            });
            walk(el);
        });
    };

    walk(doc.body);
    return doc.body.innerHTML;
};

const statusClass = (status) =>
    `mt-badge mt-badge-${(status || "Open").toLowerCase().replace(/\s+/g, "-")}`;

const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};

const TYPE_OPTIONS = [
    { value: "All", label: "All Types" },
    { value: "Bug", label: "Bug" },
    { value: "Idea", label: "Idea" },
];

// Which comments this browser has already seen, keyed by "empId|ticketId".
// Per-device by design — cross-device sync would need a server-side table.
const SEEN_KEY = "dbug_seen_comments";

const MyTicketsList = ({ empId, showPopup, onCreateTicket }) => {
    const [tickets, setTickets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState("");

    // 🔹 Filters
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("All");
    const [showTypeMenu, setShowTypeMenu] = useState(false);
    const typeDropdownRef = useRef(null);

    // 🔹 Expanded row + detail cache
    const [expandedId, setExpandedId] = useState(null);
    const [details, setDetails] = useState({});
    const [detailLoadingId, setDetailLoadingId] = useState(null);

    // 🔹 "New comment" seen-tracking, persisted so the dot stays gone after reload
    const [seen, setSeen] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch (e) { return {}; }
    });

    const markCommentSeen = (t) => {
        if (!t?.latest_comment_time) return;
        const key = `${empId}|${t.ticket_id}`;
        setSeen((prev) => {
            const prevTime = prev[key] ? new Date(prev[key]).getTime() : 0;
            if (prevTime >= new Date(t.latest_comment_time).getTime()) return prev;
            const next = { ...prev, [key]: t.latest_comment_time };
            try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
            return next;
        });
    };

    useEffect(() => {
        if (empId) loadTickets(empId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empId]);

    // Close the custom dropdown on outside click
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
                setShowTypeMenu(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    const loadTickets = async (id) => {
        setIsLoading(true);
        setLoadError("");
        try {
            const res = await apiFetch(
                `/api/tickets/my-tickets?emp_id=${encodeURIComponent(id)}`
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to load tickets");
            setTickets(data.tickets || []);
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleExpand = async (ticket) => {
        const ticketId = ticket.ticket_id;

        if (expandedId === ticketId) {
            setExpandedId(null);
            return;
        }

        markCommentSeen(ticket); // opening clears its "new comment" dot
        setExpandedId(ticketId);

        if (details[ticketId]) return; // already fetched

        setDetailLoadingId(ticketId);
        try {
            const res = await apiFetch(
                `/api/tickets/my-tickets/${encodeURIComponent(ticketId)}?emp_id=${encodeURIComponent(empId)}`
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to load ticket detail");
            setDetails((prev) => ({ ...prev, [ticketId]: data }));
        } catch (err) {
            showPopup({ icon: "error", title: "Could not open ticket", text: err.message });
            setExpandedId(null);
        } finally {
            setDetailLoadingId(null);
        }
    };

    // 🔹 Client-side filtering
    const filtered = tickets.filter((t) => {
        if (typeFilter !== "All" && t.issue_type !== typeFilter) return false;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            const hit =
                (t.ticket_id || "").toLowerCase().includes(q) ||
                (t.summary || "").toLowerCase().includes(q);
            if (!hit) return false;
        }
        return true;
    });

    const selectedTypeLabel =
        TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label || "All Types";

    return (
        <div className="mt-body">
            <div className="mt-filters">
                <input
                    type="text"
                    className="mt-search"
                    placeholder="Search by ID or summary…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                {/* Custom dropdown — replaces the native <select> */}
                <div
                    className={`type-dropdown ${showTypeMenu ? "open" : ""}`}
                    ref={typeDropdownRef}
                >
                    <div
                        className="type-selected"
                        onClick={() => setShowTypeMenu((prev) => !prev)}
                    >
                        {selectedTypeLabel}
                        <i className="fa-solid fa-angle-down"></i>
                    </div>

                    {showTypeMenu && (
                        <div className="type-menu">
                            {TYPE_OPTIONS.map((opt) => (
                                <div
                                    key={opt.value}
                                    className={`type-item ${typeFilter === opt.value ? "active" : ""}`}
                                    onClick={() => {
                                        setTypeFilter(opt.value);
                                        setShowTypeMenu(false);
                                    }}
                                >
                                    {opt.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="mt-refresh"
                    onClick={() => { setDetails({}); loadTickets(empId); }}
                    disabled={isLoading}
                    title="Refresh"
                >
                    <i className="fa-solid fa-rotate-right"></i>
                </button>
            </div>

            {isLoading && <div className="spinner"></div>}

            {loadError && (
                <p className="mt-error">
                    {loadError}{" "}
                    <span className="otp-resend-link" onClick={() => loadTickets(empId)}>Retry</span>
                </p>
            )}

            {!isLoading && !loadError && tickets.length === 0 && (
                <div className="mt-empty">
                    <div className="mt-empty-icon">🗂️</div>
                    <p>You haven't raised any tickets yet.</p>
                    <button type="button" className="mt-primary-btn" onClick={onCreateTicket}>
                        Raise your first ticket
                    </button>
                </div>
            )}

            {!isLoading && !loadError && tickets.length > 0 && filtered.length === 0 && (
                <div className="mt-empty">
                    <p>No tickets match the current filters.</p>
                    <button
                        type="button"
                        className="mt-link-btn"
                        onClick={() => { setTypeFilter("All"); setSearch(""); }}
                    >
                        Clear filters
                    </button>
                </div>
            )}

            <div className="mt-list">
                {filtered.map((t) => {
                    const isOpen = expandedId === t.ticket_id;
                    const detail = details[t.ticket_id];

                    // Flag a comment only when it's the latest activity on the
                    // ticket and this browser hasn't opened it since.
                    const seenTime = seen[`${empId}|${t.ticket_id}`];
                    const hasNewComment =
                        !!t.latest_comment &&
                        (!t.last_updated_time ||
                            new Date(t.latest_comment_time).getTime() >= new Date(t.last_updated_time).getTime()) &&
                        (!seenTime ||
                            new Date(t.latest_comment_time).getTime() > new Date(seenTime).getTime());

                    return (
                        <div key={t.ticket_id} className={`mt-item ${isOpen ? "mt-item-open" : ""}`}>
                            <button
                                type="button"
                                className="mt-item-head"
                                onClick={() => toggleExpand(t)}
                            >
                                <div className="mt-item-main">
                                    <div className="mt-item-top">
                                        <span className="mt-field-label">Ticket ID:</span>
                                        <span className="mt-ticket-id">{t.ticket_id}</span>
                                        <span className={`mt-type mt-type-${(t.issue_type || "").toLowerCase()}`}>
                                            {t.issue_type}
                                        </span>
                                        <span className={statusClass(t.ticket_status)}>{t.ticket_status}</span>
                                        {hasNewComment && (
                                            <span
                                                className="mt-newcomment"
                                                title={`New comment · ${formatDateTime(t.latest_comment_time)}`}
                                            >
                                                <span className="mt-newcomment-dot"></span>
                                                New comment
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-item-summary">
                                        <span className="mt-field-label">Subject:</span>
                                        <span className="mt-item-summary-text">{t.summary}</span>
                                    </div>
                                    <div className="mt-item-meta">
                                        <span><i className="fa-regular fa-calendar"></i> {formatDateTime(t.created_time)}</span>
                                        {t.priority_level && <span><i className="fa-solid fa-flag"></i> {t.priority_level}</span>}
                                        {t.assignee && <span><i className="fa-regular fa-user"></i> {t.assignee}</span>}
                                        {t.attachment_count > 0 && (
                                            <span><i className="fa-solid fa-paperclip"></i> {t.attachment_count}</span>
                                        )}
                                    </div>
                                </div>
                                <i className={`fa-solid fa-chevron-${isOpen ? "up" : "down"} mt-chevron`}></i>
                            </button>

                            {isOpen && (
                                <div className="mt-item-detail">
                                    {detailLoadingId === t.ticket_id && <div className="spinner"></div>}

                                    {detail && (
                                        <>
                                            <div className="mt-detail-grid">
                                                {detail.reporting_team && (
                                                    <div><span>Team</span><strong>{detail.reporting_team}</strong></div>
                                                )}
                                                {detail.testing_type && (
                                                    <div><span>Testing Type</span><strong>{detail.testing_type}</strong></div>
                                                )}
                                                {detail.device_tested?.length > 0 && (
                                                    <div><span>Devices</span><strong>{detail.device_tested.join(", ")}</strong></div>
                                                )}
                                            </div>

                                            <div className="mt-section">
                                                <h4>Description</h4>
                                                <div
                                                    className="mt-description"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(detail.description) }}
                                                />
                                            </div>

                                            {detail.attachments?.length > 0 && (
                                                <div className="mt-section">
                                                    <h4>Attachments</h4>
                                                    <div className="mt-attachments">
                                                        {detail.attachments.map((a) => (
                                                            <a
                                                                key={a.url}
                                                                href={`${API_BASE}${a.url}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="mt-attachment"
                                                            >
                                                                <i className="fa-solid fa-paperclip"></i> {a.name}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-section">
                                                <h4>Status History</h4>
                                                <div className="mt-timeline">
                                                    <div className="mt-timeline-item">
                                                        <span className="mt-dot"></span>
                                                        <div>
                                                            <strong>Submitted</strong>
                                                            <span className="mt-timeline-time">{formatDateTime(detail.created_time)}</span>
                                                        </div>
                                                    </div>
                                                    {detail.status_history?.map((s, i) => (
                                                        <div className="mt-timeline-item" key={i}>
                                                            <span className="mt-dot"></span>
                                                            <div>
                                                                <strong>{s.ticket_status}</strong>
                                                                <span className="mt-timeline-time">{formatDateTime(s.updated_time)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="mt-section">
                                                <h4>Comments {detail.comments?.length > 0 && `(${detail.comments.length})`}</h4>
                                                {detail.comments?.length > 0 ? (
                                                    <div className="mt-comments">
                                                        {detail.comments.map((c, i) => (
                                                            <div className="mt-comment" key={i}>
                                                                <p>{c.ticket_comments}</p>
                                                                <span className="mt-comment-time">{formatDateTime(c.added_time)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="mt-none">No comments yet.</p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MyTicketsList;
